/**
 * The rail's stacking pass, kept pure so it can be tested as arithmetic.
 *
 * The mechanism is the design mockup's, verbatim (`Review.dc.html`): each card
 * wants to sit level with its block — its *anchor* — and takes the lower of
 * that and the bottom of the card above it plus a gutter. One forward sweep,
 * greedy, never upward, never overlapping. Card heights are the measured DOM
 * heights wherever the caller has them, an estimate until it does.
 *
 * The open composer is not a special case: the caller splices it into the same
 * queue at its block's position, so opening one pushes every card below it
 * down instead of landing on top of a card.
 */

export const RAIL = {
  /** Cards sit 6px below the block's exact top, so the connector hairline
   *  meets the block's edge rather than its corner. */
  anchorNudge: 6,
  /** Minimum daylight between stacked cards. */
  gutter: 14,
  /** Where the sweep's cursor starts. Negative by exactly `gutter - nudge`,
   *  so the first card can sit at its raw anchor. */
  cursorStart: -8
} as const;

export interface RailItem {
  /** Thread id, or `'__composer'` for the open composer. */
  id: string;
  /** The anchoring block's top, in course content space. */
  blockTop: number;
  /** Measured height when the DOM has answered, an estimate before that. */
  height: number;
}

export interface PlacedItem {
  id: string;
  /** In track space: content-space top translated by the origin delta. */
  top: number;
}

/** `blockTop` translated into the rail track's own coordinate space. */
export function anchorFor(blockTop: number, originDelta: number): number {
  return blockTop + originDelta + RAIL.anchorNudge;
}

/**
 * Place every item. `items` must already be in document block order, with the
 * composer spliced in — see `spliceByBlockOrder`.
 */
export function stackCards(items: readonly RailItem[], originDelta: number): PlacedItem[] {
  let cursor = RAIL.cursorStart;
  const placed: PlacedItem[] = [];
  for (const item of items) {
    const top = Math.max(anchorFor(item.blockTop, originDelta), cursor + RAIL.gutter);
    cursor = top + item.height;
    placed.push({ id: item.id, top });
  }
  return placed;
}

/**
 * Insert `extra` into `ordered` at the position its block occupies in the
 * document: before the first item whose block comes later. Ties — a composer
 * on a block that already has cards — land *after* that block's cards, which
 * is where a new thread will appear once posted.
 */
export function spliceByBlockOrder<T>(
  ordered: readonly T[],
  extra: T,
  orderOf: (item: T) => number
): T[] {
  const at = ordered.findIndex((item) => orderOf(item) > orderOf(extra));
  const out = [...ordered];
  out.splice(at === -1 ? out.length : at, 0, extra);
  return out;
}
